
import React, { useEffect, useMemo, useState } from "react";
import {
  Edit3,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { api, resolveMediaUrl } from "../../lib/api";

/* -------------------------------------------------------
   FORM HELPERS
------------------------------------------------------- */

function emptyValues(fields) {
  return Object.fromEntries(
    fields.map((field) => [
      field.name,
      field.defaultValue ??
        (field.type === "checkbox" ? false : ""),
    ])
  );
}

function getValue(item, name) {
  return (
    name
      .split(".")
      .reduce((value, key) => value?.[key], item) ?? ""
  );
}

function setNested(target, name, value) {
  const keys = name.split(".");
  let cursor = target;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }

    if (!cursor[key] || typeof cursor[key] !== "object") {
      cursor[key] = {};
    }

    cursor = cursor[key];
  });
}

function isFieldVisible(field, values) {
  if (!field.showWhen) return true;

  const controllingValue = values[field.showWhen.field];

  if (Array.isArray(field.showWhen.in)) {
    return field.showWhen.in.includes(controllingValue);
  }

  if (
    Object.prototype.hasOwnProperty.call(
      field.showWhen,
      "equals"
    )
  ) {
    return controllingValue === field.showWhen.equals;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      field.showWhen,
      "notEquals"
    )
  ) {
    return controllingValue !== field.showWhen.notEquals;
  }

  return true;
}

function isFieldRequired(field, editingId) {
  if (field.required) return true;

  if (field.requiredOnCreate) {
    return !editingId;
  }

  if (field.requiredWhenVisible) {
    return true;
  }

  return false;
}

function isEmptyValue(value) {
  return (
    value === "" ||
    value === null ||
    value === undefined
  );
}

function normalizeOption(option) {
  if (typeof option === "object" && option !== null) {
    return {
      label: option.label,
      value: option.value,
    };
  }

  return {
    label: option,
    value: option,
  };
}

function getApiRecords(response) {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  return [];
}

/* -------------------------------------------------------
   CRUD MANAGER
------------------------------------------------------- */

export default function CrudManager({
  title,
  singular,
  description,
  fields,
  apiResource,
  columns,
  modalClassName = "",
}) {
  const [items, setItems] = useState([]);

  const [form, setForm] = useState(() =>
    emptyValues(fields)
  );

  const [editingId, setEditingId] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const visibleFields = useMemo(
    () =>
      fields.filter((field) =>
        isFieldVisible(field, form)
      ),
    [fields, form]
  );

  /* -------------------------------------------------------
     LOAD RECORDS
  ------------------------------------------------------- */

  async function load() {
    setLoading(true);
    setError("");

    try {
      const response = await apiResource.list();
      const records = getApiRecords(response);

      setItems(records);
    } catch (loadError) {
      console.error(`${singular} load failed:`, loadError);

      setError(
        loadError?.message || "Unable to load records."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------------------------------------------
     MODAL ACTIONS
  ------------------------------------------------------- */

  function closeModal() {
    if (saving) return;

    setOpen(false);
    setEditingId("");
    setEditingItem(null);
    setForm(emptyValues(fields));
    setError("");
  }

  useEffect(() => {
    if (!open) return undefined;

    function handleEscape(event) {
      if (event.key === "Escape" && !saving) {
        closeModal();
      }
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saving]);

  /* -------------------------------------------------------
     FORM ACTIONS
  ------------------------------------------------------- */

  function updateField(fieldName, value) {
    setForm((current) => ({
      ...current,
      [fieldName]: value,
    }));
  }

  function startCreate() {
    setEditingId("");
    setEditingItem(null);
    setForm(emptyValues(fields));
    setMessage("");
    setError("");
    setOpen(true);
  }

  function startEdit(item) {
    const values = emptyValues(fields);

    fields.forEach((field) => {
      let value = getValue(item, field.name);

      if (field.type === "date" && value) {
        value = String(value).slice(0, 10);
      }

      if (field.type === "checkbox") {
        value = Boolean(value);
      }

      values[field.name] = value;
    });

    setEditingId(item._id);
    setEditingItem(item);
    setForm(values);
    setMessage("");
    setError("");
    setOpen(true);
  }

  /* -------------------------------------------------------
     IMAGE / DOCUMENT UPLOAD
  ------------------------------------------------------- */

  async function upload(fieldName, file, kind = "image") {
    if (!file) return;

    setSaving(true);
    setError("");

    try {
      const response =
        kind === "file"
          ? await api.uploadDocument(file)
          : await api.uploadImage(file);

      const uploadedUrl =
        response?.data?.data?.url ||
        response?.data?.url ||
        response?.url ||
        "";

      if (!uploadedUrl) {
        throw new Error(
          "Uploaded file URL was not received."
        );
      }

      updateField(fieldName, uploadedUrl);

    } catch (uploadError) {
      console.error(
        `${singular} file upload failed:`,
        uploadError
      );

      setError(
        uploadError?.message ||
          "Unable to upload the file."
      );
    } finally {
      setSaving(false);
    }
  }

  /* -------------------------------------------------------
     VALIDATE VISIBLE FIELDS
  ------------------------------------------------------- */

  function validateVisibleFields() {
    for (const field of visibleFields) {
      const required = isFieldRequired(
        field,
        editingId
      );

      if (!required) continue;

      let value = form[field.name];

      if (
        editingId &&
        editingItem &&
        (field.type === "file" ||
          field.type === "image") &&
        isEmptyValue(value)
      ) {
        value = getValue(
          editingItem,
          field.name
        );
      }

      if (
        typeof value === "string" &&
        value.trim() === ""
      ) {
        value = "";
      }

      if (isEmptyValue(value)) {
        throw new Error(
          `${field.label} is required.`
        );
      }
    }
  }

  /* -------------------------------------------------------
     CREATE PAYLOAD
  ------------------------------------------------------- */

  function createPayload() {
    const payload = {};

    fields.forEach((field) => {
      const fieldVisible = isFieldVisible(
        field,
        form
      );

      // Hidden fields are not included in the payload.
      if (!fieldVisible) return;

      let value = form[field.name];

      // Preserve existing files while editing.
      if (
        editingId &&
        editingItem &&
        (field.type === "file" ||
          field.type === "image") &&
        isEmptyValue(value)
      ) {
        const existingValue = getValue(
          editingItem,
          field.name
        );

        if (!isEmptyValue(existingValue)) {
          value = existingValue;
        }
      }

      if (field.type === "number") {
        value = isEmptyValue(value)
          ? 0
          : Number(value);
      }

      if (field.type === "checkbox") {
        value = Boolean(value);
      }

      if (field.type === "date" && !value) {
        value = null;
      }

      if (
        typeof value === "string" &&
        field.type !== "file" &&
        field.type !== "image"
      ) {
        value = value.trim();
      }

      setNested(payload, field.name, value);
    });

    return payload;
  }

  /* -------------------------------------------------------
     SUBMIT FORM
  ------------------------------------------------------- */

  async function submit(event) {
    event.preventDefault();

    if (saving) return;

    setSaving(true);
    setError("");
    setMessage("");

    let payload;

    try {
      validateVisibleFields();

      payload = createPayload();

      // Correct module-specific logging.
      console.log(`${singular} payload:`, payload);

      if (editingId) {
        await apiResource.update(
          editingId,
          payload
        );
      } else {
        await apiResource.create(payload);
      }

      setMessage(
        `${singular} saved successfully.`
      );

      setOpen(false);
      setEditingId("");
      setEditingItem(null);
      setForm(emptyValues(fields));

      await load();

    } catch (saveError) {
      console.error(`${singular} save failed:`, {
        message: saveError?.message,
        payload,
        error: saveError,
      });

      setError(
        saveError?.message ||
          `Unable to save ${singular.toLowerCase()}.`
      );
    } finally {
      setSaving(false);
    }
  }

  /* -------------------------------------------------------
     DELETE RECORD
  ------------------------------------------------------- */

  async function remove(item) {
    const confirmed = window.confirm(
      `Delete this ${singular.toLowerCase()}?`
    );

    if (!confirmed) return;

    setError("");
    setMessage("");

    try {
      await apiResource.remove(item._id);

      setMessage(
        `${singular} deleted successfully.`
      );

      await load();
    } catch (deleteError) {
      console.error(
        `${singular} deletion failed:`,
        deleteError
      );

      setError(
        deleteError?.message ||
          `Unable to delete ${singular.toLowerCase()}.`
      );
    }
  }

  /* -------------------------------------------------------
     RENDER FORM FIELDS
  ------------------------------------------------------- */

  function renderField(field) {
    const value = form[field.name];

    const required = isFieldRequired(
      field,
      editingId
    );

    if (field.type === "textarea") {
      return (
        <textarea
          rows={field.rows || 4}
          required={required}
          placeholder={field.placeholder || ""}
          value={value ?? ""}
          onChange={(event) =>
            updateField(
              field.name,
              event.target.value
            )
          }
        />
      );
    }

    if (field.type === "select") {
      return (
        <select
          required={required}
          value={value ?? ""}
          onChange={(event) =>
            updateField(
              field.name,
              event.target.value
            )
          }
        >
          {field.placeholder && (
            <option value="">
              {field.placeholder}
            </option>
          )}

          {(field.options || []).map((option) => {
            const normalized = normalizeOption(option);

            return (
              <option
                key={String(normalized.value)}
                value={normalized.value}
              >
                {normalized.label}
              </option>
            );
          })}
        </select>
      );
    }

    if (field.type === "checkbox") {
      return (
        <div className="admin-check">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) =>
              updateField(
                field.name,
                event.target.checked
              )
            }
          />

          <em>
            {field.help || "Show on website"}
          </em>
        </div>
      );
    }

    if (field.type === "image") {
      const existingImage =
        value ||
        (editingItem
          ? getValue(
              editingItem,
              field.name
            )
          : "");

      return (
        <div className="admin-upload">
          <input
            type="file"
            accept={
              field.accept ||
              "image/jpeg,image/png,image/webp,image/gif"
            }
            disabled={saving}
            onChange={(event) => {
              const selectedFile =
                event.target.files?.[0];

              upload(
                field.name,
                selectedFile,
                "image"
              );

              event.target.value = "";
            }}
          />

          {existingImage && (
            <div className="admin-upload-preview">
              <img
                src={resolveMediaUrl(
                  existingImage
                )}
                alt="Uploaded preview"
              />
            </div>
          )}
        </div>
      );
    }

    if (field.type === "file") {
      const existingFile =
        value ||
        (editingItem
          ? getValue(
              editingItem,
              field.name
            )
          : "");

      return (
        <div className="admin-upload">
          <input
            type="file"
            accept={
              field.accept ||
              ".pdf,application/pdf"
            }
            disabled={saving}
            onChange={(event) => {
              const selectedFile =
                event.target.files?.[0];

              upload(
                field.name,
                selectedFile,
                "file"
              );

              event.target.value = "";
            }}
          />

          {saving && (
            <small className="admin-field-help">
              Uploading document...
            </small>
          )}

          {existingFile && (
            <a
              href={resolveMediaUrl(
                existingFile
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="admin-file-link"
            >
              View uploaded PDF
            </a>
          )}
        </div>
      );
    }

    return (
      <input
        type={field.type || "text"}
        min={field.min}
        max={field.max}
        step={field.step}
        required={required}
        placeholder={field.placeholder || ""}
        value={value ?? ""}
        onChange={(event) =>
          updateField(
            field.name,
            event.target.value
          )
        }
      />
    );
  }

  /* -------------------------------------------------------
     COMPONENT RENDER
  ------------------------------------------------------- */

  return (
    <section className="admin-page">

      {/* PAGE HEADER */}
      <div className="admin-page-head">
        <div>
          <h2>{title}</h2>
          <p>
            {description ||
              `Manage ${title.toLowerCase()} displayed on the website.`}
          </p>
        </div>

        <button
          type="button"
          className="admin-primary"
          onClick={startCreate}
        >
          <Plus size={17} />
          Add {singular}
        </button>
      </div>

      {/* ALERTS */}
      {message && (
        <div className="admin-alert success">
          {message}
        </div>
      )}

      {error && (
        <div className="admin-alert error">
          {error}
        </div>
      )}

      {/* RECORDS TABLE */}
      <div className="admin-card">
        {loading ? (
          <div className="admin-empty">
            <RefreshCw
              className="spin"
              size={20}
            />
            Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="admin-empty">
            No records added yet.
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.label}>
                      {column.label}
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    {columns.map((column) => (
                      <td key={column.label}>
                        {column.render
                          ? column.render(item)
                          : getValue(
                              item,
                              column.accessor
                            ) || "—"}
                      </td>
                    ))}

                    <td>
                      <div className="admin-actions">
                        <button
                          type="button"
                          onClick={() =>
                            startEdit(item)
                          }
                          aria-label={`Edit ${singular}`}
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>

                        <button
                          type="button"
                          className="danger"
                          onClick={() =>
                            remove(item)
                          }
                          aria-label={`Delete ${singular}`}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD / EDIT MODAL */}
      {open && (
        <div
          className="admin-modal-backdrop"
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              closeModal();
            }
          }}
        >
          <form
            className={`admin-modal ${modalClassName}`}
            onSubmit={submit}
          >
            <div className="admin-modal-head">
              <h3>
                {editingId
                  ? `Edit ${singular}`
                  : `Add ${singular}`}
              </h3>

              <button
                type="button"
                className="admin-icon-btn"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close form"
              >
                <X size={19} />
              </button>
            </div>

            <div className="admin-form">
              {visibleFields.map((field) => {
                const required =
                  isFieldRequired(
                    field,
                    editingId
                  );

                return (
                  <label
                    key={field.name}
                    className={
                      field.fullWidth
                        ? "full"
                        : ""
                    }
                  >
                    <span>
                      {field.label}
                      {required ? " *" : ""}
                    </span>

                    {renderField(field)}

                    {field.help &&
                      field.type !==
                        "checkbox" && (
                        <small className="admin-field-help">
                          {field.help}
                        </small>
                      )}
                  </label>
                );
              })}
            </div>

            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-secondary"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="admin-primary"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingId
                    ? `Update ${singular}`
                    : `Save ${singular}`}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
